import React from 'react';
import PropTypes from 'prop-types';
import NextLink from 'next/link';
import { observer } from 'mobx-react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import useStores from '../../../utils/useStores';
import LaunchIcon from '@material-ui/icons/Launch';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0, 2),
  },
  noPadding: {
    padding: 0,
  },
  border: {
    '&:last-child': {
      borderRight: 'none',
    },
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
  link: {
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&$internal': {
      color: theme.palette.primary.main,
    },
    '&$external': {
      whiteSpace: 'nowrap',
    },
  },
  internal: {},
  external: {},
}));

export const ExternalIcon = () => (
  <LaunchIcon fontSize="inherit" style={{ verticalAlign: 'sub' }} />
);

const basedOnLogin = isLoggedIn => i => (i.internal ? isLoggedIn : true);
const withoutHidden = i => !i.hideInNav;

const Link = props => {
  const { item, passHref, ...rest } = props;
  if (item.external) {
    return (
      <a href={item.link} target="_blank" rel="noopener noreferrer" {...rest} />
    );
  }
  const linkContext = process.env.NODE_ENV === 'production' ? '/' : '/depi/';
  return (
    <NextLink
      href={
        item.uuid ? `${linkContext}[...pageId]` : `${linkContext}${item.link}`
      }
      as={`${linkContext}${item.link}`}
      passHref={passHref}
      {...rest}
    />
  );
};

Link.propTypes = {
  item: PropTypes.object.isRequired,
};

const NavItems = props => {
  const { items } = props;
  const classes = useStyles();
  return (
    <React.Fragment>
      {items.map(item => (
        <React.Fragment key={item.label}>
          <Link item={item} passHref>
            <Typography
              component={item.external ? 'div' : 'a'}
              variant="subtitle1"
              className={cx(classes.link, {
                [classes.internal]: item.internal,
                [classes.external]: item.external,
              })}
            >
              {item.label}&nbsp;
              {item.external && <ExternalIcon />}
            </Typography>
          </Link>
          {item.items && (
            <Box ml={1} mb={1} display="flex" flexDirection="column">
              <NavItems items={item.items} />
            </Box>
          )}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};

NavItems.propTypes = {
  items: PropTypes.array.isRequired,
};

const NavSection = props => {
  const classes = useStyles();
  const { section, noBorder } = props;
  const {
    loginStore: { isLoggedIn },
  } = useStores();
  const className = cx(classes.root, { [classes.border]: !noBorder });
  if (Array.isArray(section)) {
    return (
      <Box className={cx(className, classes.noPadding)}>
        {section.map(s => (
          <NavSection key={s.title} noBorder section={s} />
        ))}
      </Box>
    );
  }
  return (
    <Box className={className}>
      <Typography variant="h6" color="primary" gutterBottom>
        {section.title}
      </Typography>
      <Box display="flex" flexDirection="column">
        {section.items && (
          <NavItems
            items={section.items
              .filter(withoutHidden)
              .filter(basedOnLogin(isLoggedIn))}
          />
        )}
      </Box>
    </Box>
  );
};

NavSection.propTypes = {
  section: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  noBorder: PropTypes.bool,
};

NavSection.defaultProps = {
  noBorder: false,
};

export default observer(NavSection);
